// @ts-nocheck
import _ from 'lodash';
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  makeStyles,
  Box,
  Grid,
  Icon,
  TextField,
  Button,
  Typography,
  MenuItem,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { useIfMounted } from '../../../../common/hooks';
import { StringUtils } from '../../../../common/utils';

const PRICE_FACTOR = 12;

const useStyles = makeStyles(theme => ({
  container: {
    flex: '1',
    display: 'flex',
  },
  form: {
    flex: '1',
    display: 'flex',
    flexDirection: 'column',
    '& .MuiGrid-item': {
      marginBottom: theme.spacing(1),
      '&:last-child': {
        marginBottom: '0',
      },
    },
  },
  formContent: {
    flex: '1',
    display: 'flex',
    flexWrap: 'wrap',
    padding: theme.spacing(1, 3),
    overflow: 'auto',
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    padding: theme.spacing(1),
  },
  button: {
    margin: theme.spacing(1),
    '&:last-child': {
      marginRight: '0',
    },
  },
}));

function ContractForm(props) {
  const [error, setError] = useState(false);
  const [t] = useTranslation();
  const {
    company,
    products,
    onSave,
    onCancel,
    actionsDisabled,
    cancelButtonText,
    cancelButtonIcon,
    saveButtonText,
    saveButtonIcon,
  } = props;
  const { formState, reset, register, handleSubmit, setValue } = useForm();
  let product = products ? products[0] : null;
  let errorMessage = '';
  const classes = useStyles();
  const ifMounted = useIfMounted();

  const { isSubmitting } = formState;

  const getProductPrice = () =>
    product.durationDays === 365 ? product.priceWithoutVat : product.priceWithoutVat * PRICE_FACTOR;

  const getFormattedProductPrice = () =>
    StringUtils.getFormattedSum(product ? getProductPrice() : '');

  const onSubmit = async data => {
    try {
      const formData = {
        companyId: company.id,
        productId: product.id,
        customerAccount: `P-${company.id}`,
        invoiceAccount: `P-${company.id}`,
        productName: product.title,
        unitPrice: getProductPrice(),
        duration: 365,
        status: 'NEW',
      };
      await onSave(formData);
      ifMounted(() => reset(data));
    } catch (e) {
      setError(true);
      errorMessage = e.message;
    }
  };

  const handleProductChange = e => {
    if (products) {
      product = products.find(x => x.id === e.target.value);
      setValue('productId', product.id);
      setValue('unitPrice', getFormattedProductPrice());
    }
  };

  return (
    <Box id="ContractForm-container" className={classes.container}>
      <form id="ContractForm-form" className={classes.form} onSubmit={handleSubmit(onSubmit)}>
        {error && (
          <div className="alert alert-danger mt-3 mb-0">
            <h4>{t('contract.contractCreateError')}</h4>
            <p>{errorMessage}</p>
          </div>
        )}
        <Box className={classes.formContent}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                id="ContractForm-customerAccount"
                name="customerAccount"
                label={t('contract.contractCustomerAccount')}
                variant="outlined"
                defaultValue={`P-${company.id}`}
                inputRef={register}
                disabled
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                id="ContractForm-invoiceAccount"
                name="invoiceAccount"
                label={t('contract.contractInvoiceAccount')}
                variant="outlined"
                defaultValue={`P-${company.id}`}
                inputRef={register}
                disabled
                fullWidth
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                id="ContractForm-productId"
                name="productId"
                label={t('contract.contractProductId')}
                variant="outlined"
                defaultValue={product?.id}
                inputRef={register}
                fullWidth
                disabled
              />
            </Grid>
            <Grid item xs={8}>
              <TextField
                id="ContractForm-productName"
                label={t('contract.contractProductName')}
                variant="outlined"
                fullWidth
                select
                defaultValue={product?.id}
                onChange={handleProductChange}
              >
                {products?.map(prod => (
                  <MenuItem key={prod.id} value={prod.id}>
                    {prod.title}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                id="ContractForm-unitPrice"
                name="unitPrice"
                label={t('contract.contractUnitPrice')}
                variant="outlined"
                defaultValue={getFormattedProductPrice()}
                inputRef={register}
                fullWidth
                disabled
              />
            </Grid>
          </Grid>
        </Box>
        <Box className={classes.actions}>
          <Button
            id="ContractForm-cancel"
            className={classes.button}
            startIcon={<Icon>{_.defaultTo(cancelButtonIcon, 'cancel')}</Icon>}
            onClick={onCancel}
            disabled={actionsDisabled || isSubmitting}
          >
            <Typography variant="button" noWrap>
              {_.defaultTo(cancelButtonText, t('common.cancel'))}
            </Typography>
          </Button>
          <Button
            id="ContractForm-save"
            type="submit"
            color="primary"
            className={classes.button}
            startIcon={<Icon>{_.defaultTo(saveButtonIcon, 'save')}</Icon>}
            disabled={actionsDisabled || isSubmitting}
          >
            <Typography variant="button" noWrap>
              {_.defaultTo(saveButtonText, t('common.save'))}
            </Typography>
          </Button>
        </Box>
      </form>
    </Box>
  );
}

ContractForm.defaultProps = {
  cancelButtonText: undefined,
  cancelButtonIcon: undefined,
  saveButtonText: undefined,
  saveButtonIcon: undefined,
  actionsDisabled: undefined,
};

ContractForm.propTypes = {
  company: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    cvrNumber: PropTypes.number,
    eanNumber: PropTypes.string,
    status: PropTypes.string,
    segment: PropTypes.string,
    department: PropTypes.string,
    address: PropTypes.string,
    zipcode: PropTypes.string,
    city: PropTypes.string,
    country: PropTypes.string,
    phone: PropTypes.string,
    notes: PropTypes.string,
    invoiceEmail: PropTypes.string,
    contactName: PropTypes.string,
    contactPhone: PropTypes.string,
    contactEmail: PropTypes.string,
    contracts: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
  products: PropTypes.arrayOf(PropTypes.object).isRequired,
  cancelButtonText: PropTypes.string,
  cancelButtonIcon: PropTypes.string,
  saveButtonText: PropTypes.string,
  saveButtonIcon: PropTypes.string,
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  actionsDisabled: PropTypes.bool,
};

export default ContractForm;
